// components/voice/VoiceSelector.tsx
import { useRef, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';
import { GEMINI_VOICES, GeminiVoiceId } from '../../contexts/voice/types';

interface VoiceSelectorProps {
  selectedVoice: GeminiVoiceId;
  onSelect: (voice: GeminiVoiceId) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  className?: string;
}

export function VoiceSelector({
  selectedVoice,
  onSelect,
  isOpen,
  onToggle,
  onClose,
  className = '',
}: VoiceSelectorProps) {
  const selectorRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const currentVoiceInfo = GEMINI_VOICES.find(v => v.id === selectedVoice) || GEMINI_VOICES[0];

  const handleSelect = (voiceId: GeminiVoiceId) => {
    onSelect(voiceId);
    onClose();
  };

  return (
    <div className={`relative flex flex-col items-end ${className}`}>
      {/* Dropdown */}
      {isOpen && (
        <div
          ref={selectorRef}
          className="absolute bottom-full mb-2 bg-card/95 backdrop-blur-sm border border-primary/20 rounded-2xl shadow-2xl p-3 animate-fade-in w-64"
        >
          <div className="text-xs font-medium text-muted-foreground mb-2 px-1">Kies een stem</div>
          <div className="space-y-1">
            {GEMINI_VOICES.map((voice) => (
              <button
                key={voice.id}
                onClick={() => handleSelect(voice.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all ${
                  selectedVoice === voice.id
                    ? 'bg-primary/10 border border-primary/30'
                    : 'hover:bg-muted/50'
                }`}
              >
                <span className="text-lg">{voice.flag}</span>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-foreground">{voice.name}</div>
                  <div className="text-xs text-muted-foreground">{voice.description}</div>
                </div>
                {selectedVoice === voice.id && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="relative p-3 rounded-full bg-card border border-primary/20 shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-1"
        title="Kies stem"
      >
        <span className="text-lg">{currentVoiceInfo.flag}</span>
        <ChevronUp className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}
